/*
  Dedicated Cart enhancement script.
  Shopify remains the source of truth for totals and discounts, so every cart
  mutation requests fresh section HTML instead of recalculating prices in JS.
*/
(function () {
  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  }

  onReady(function () {
    var pendingMutations = new Map();
    var fulfillmentMutationPending = false;

    function getCartRoot() {
      return document.querySelector('[data-cart-section-root]');
    }

    function getLineElements(root) {
      if (!root) {
        return [];
      }

      return Array.prototype.slice.call(root.querySelectorAll('[data-cart-line-key]'));
    }

    function findLineElement(lineKey, lineReference) {
      var root = getCartRoot();
      var lineElements = getLineElements(root);
      var matchedByKey = lineElements.find(function (lineElement) {
        return lineElement.getAttribute('data-cart-line-key') === lineKey;
      });

      if (matchedByKey) {
        return matchedByKey;
      }

      if (!lineReference) {
        return null;
      }

      return (
        lineElements.find(function (lineElement) {
          return lineElement.getAttribute('data-line-reference') === lineReference;
        }) || null
      );
    }

    function getCartStatus(root) {
      return root ? root.querySelector('[data-cart-status]') : null;
    }

    function getLiveRegion(root) {
      return root ? root.querySelector('[data-cart-live-region]') : null;
    }

    function getPromoPanel(root) {
      return root ? root.querySelector('[data-cart-promo-panel]') : null;
    }

    function getPromoToggle(root) {
      return root ? root.querySelector('[data-cart-promo-toggle]') : null;
    }

    function getPromoInput(root) {
      return root ? root.querySelector('[data-cart-discount-input]') : null;
    }

    function getPromoError(root) {
      return root ? root.querySelector('[data-cart-discount-error]') : null;
    }

    function getFulfillmentStatus(root) {
      return root ? root.querySelector('[data-cart-fulfillment-status]') : null;
    }

    function getFulfillmentInputs(root) {
      if (!root) {
        return [];
      }

      return Array.prototype.slice.call(root.querySelectorAll('[data-cart-fulfillment-option]'));
    }

    function parseCartAttributes(root) {
      var rawValue;
      var parsedValue;

      if (!root) {
        return {};
      }

      rawValue = root.getAttribute('data-cart-attributes');

      if (!rawValue) {
        return {};
      }

      try {
        parsedValue = JSON.parse(rawValue);
      } catch (error) {
        return {};
      }

      if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
        return {};
      }

      return parsedValue;
    }

    function clearStatus(root) {
      var status = getCartStatus(root);

      if (!status) {
        return;
      }

      status.hidden = true;
      status.textContent = '';
    }

    function showStatus(root, message) {
      var status = getCartStatus(root);

      if (!status || !message) {
        return;
      }

      status.textContent = message;
      status.hidden = false;
    }

    function setFulfillmentStatus(root, message, state) {
      var status = getFulfillmentStatus(root);

      if (!status) {
        return;
      }

      status.textContent = message || '';
      status.hidden = !message;

      if (!message) {
        status.removeAttribute('data-state');
        return;
      }

      status.setAttribute('data-state', state || 'success');
    }

    function clearFulfillmentStatus(root) {
      setFulfillmentStatus(root, '', '');
    }

    function announce(root, message) {
      var liveRegion = getLiveRegion(root);

      if (!liveRegion) {
        return;
      }

      liveRegion.textContent = '';

      window.setTimeout(function () {
        liveRegion.textContent = message || '';
      }, 30);
    }

    function setPromoExpanded(root, expanded) {
      var promoPanel = getPromoPanel(root);
      var promoToggle = getPromoToggle(root);

      if (!promoPanel || !promoToggle) {
        return;
      }

      promoPanel.hidden = !expanded;
      promoToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    function setPromoError(root, message) {
      var promoError = getPromoError(root);

      if (!promoError) {
        return;
      }

      promoError.textContent = message || '';
      promoError.hidden = !message;
    }

    function setFulfillmentPending(root, pending) {
      getFulfillmentInputs(root).forEach(function (input) {
        input.disabled = pending;
      });
    }

    function getCheckoutButtons(root) {
      if (!root) {
        return [];
      }

      return Array.prototype.slice.call(root.querySelectorAll('[data-cart-checkout-button]'));
    }

    function getFulfillmentRequiredMessage(root) {
      return root ? root.querySelector('[data-cart-fulfillment-required-message]') : null;
    }

    function hasFulfillmentSelection(root) {
      return getFulfillmentInputs(root).some(function (input) {
        return input.checked;
      });
    }

    function syncCheckoutEligibility(root) {
      var hasSelection = hasFulfillmentSelection(root);
      var requiredMessage = getFulfillmentRequiredMessage(root);

      getCheckoutButtons(root).forEach(function (button) {
        /*
          Soft-gated buttons (cart drawer) stay clickable so a tap can lead
          the shopper to the missing fulfillment choice.
        */
        if (!button.hasAttribute('data-cart-checkout-soft-gate')) {
          button.disabled = !hasSelection;
        }
        button.setAttribute('aria-disabled', hasSelection ? 'false' : 'true');
      });

      if (root) {
        root.querySelectorAll('[data-cart-fulfillment-fieldset]').forEach(function (fieldset) {
          fieldset.classList.toggle('has-selection', hasSelection);
        });
      }

      if (!requiredMessage) {
        return;
      }

      requiredMessage.hidden = hasSelection;
    }

    function setLineError(lineElement, message) {
      var errorNode = lineElement ? lineElement.querySelector('[data-line-error]') : null;

      if (!errorNode) {
        return;
      }

      errorNode.textContent = message || '';
      errorNode.hidden = !message;
    }

    function clearLineError(lineElement) {
      setLineError(lineElement, '');
    }

    function syncLineQuantityControls(lineElement) {
      var quantityInput = getLineQuantityInput(lineElement);
      var quantityButtons;
      var currentQuantity;
      var min;
      var max;

      if (!lineElement || !quantityInput) {
        return;
      }

      quantityButtons = lineElement.querySelectorAll('[data-cart-quantity-change]');
      currentQuantity = getLineQuantity(lineElement);
      min = getLineMin(lineElement);
      max = getLineMax(lineElement);

      quantityButtons.forEach(function (button) {
        var delta = Number(button.getAttribute('data-cart-quantity-change') || 0);
        var shouldDisable = false;

        if (delta < 0 && currentQuantity <= min) {
          shouldDisable = true;
        }

        if (delta > 0 && max !== null && Number.isFinite(max) && currentQuantity >= max) {
          shouldDisable = true;
        }

        button.disabled = shouldDisable;
        button.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
      });
    }

    function syncAllLineQuantityControls(root) {
      getLineElements(root).forEach(function (lineElement) {
        syncLineQuantityControls(lineElement);
      });
    }

    function showInventoryLimitFeedback(root, lineElement) {
      var inventoryMessage = root ? root.getAttribute('data-status-inventory-error') : '';

      if (!inventoryMessage) {
        return;
      }

      showStatus(root, inventoryMessage);
      announce(root, inventoryMessage);
      setLineError(lineElement, inventoryMessage);
    }

    function setLinePending(lineElement, pending) {
      var controls;

      if (!lineElement) {
        return;
      }

      lineElement.setAttribute('aria-busy', pending ? 'true' : 'false');
      controls = lineElement.querySelectorAll(
        '[data-cart-quantity-change], [data-cart-quantity-input], [data-cart-remove-line]'
      );

      controls.forEach(function (control) {
        if ('disabled' in control) {
          control.disabled = pending;
        }

        if (control.matches('a')) {
          control.setAttribute('aria-disabled', pending ? 'true' : 'false');
        }
      });
    }

    function getLineStep(lineElement) {
      return Number(lineElement.getAttribute('data-line-step') || 1) || 1;
    }

    function getLineMin(lineElement) {
      return Number(lineElement.getAttribute('data-line-min') || 0) || 0;
    }

    function getLineMax(lineElement) {
      var rawValue = lineElement.getAttribute('data-line-max');

      if (!rawValue) {
        return null;
      }

      return Number(rawValue);
    }

    function getLineQuantityInput(lineElement) {
      return lineElement ? lineElement.querySelector('[data-cart-quantity-input]') : null;
    }

    function getLineQuantity(lineElement) {
      var quantityInput = getLineQuantityInput(lineElement);
      var parsedQuantity = quantityInput ? Number(quantityInput.value) : 0;

      return Number.isFinite(parsedQuantity) ? parsedQuantity : 0;
    }

    function clampLineQuantity(lineElement, nextQuantity) {
      var step = getLineStep(lineElement);
      var min = getLineMin(lineElement);
      var max = getLineMax(lineElement);
      var normalizedQuantity = Number(nextQuantity);

      if (!Number.isFinite(normalizedQuantity)) {
        normalizedQuantity = getLineQuantity(lineElement) || min || step;
      }

      normalizedQuantity = Math.round(normalizedQuantity / step) * step;

      if (normalizedQuantity < min) {
        normalizedQuantity = min;
      }

      if (max !== null && Number.isFinite(max) && normalizedQuantity > max) {
        normalizedQuantity = max;
      }

      return normalizedQuantity;
    }

    function parseCartApiError(payload, fallbackMessage) {
      var normalizedPayloadMessage;

      if (!payload) {
        return fallbackMessage || '';
      }

      normalizedPayloadMessage = buildCartApiErrorText(payload);

      if (isInventoryLimitError(payload)) {
        return fallbackMessage || normalizedPayloadMessage || '';
      }

      if (typeof payload.description === 'string' && payload.description !== '') {
        return payload.description;
      }

      if (typeof payload.message === 'string' && payload.message !== '') {
        return payload.message;
      }

      return fallbackMessage || '';
    }

    function buildCartApiErrorText(payload) {
      var parts = [];

      if (!payload) {
        return '';
      }

      if (typeof payload.description === 'string' && payload.description !== '') {
        parts.push(payload.description);
      }

      if (typeof payload.message === 'string' && payload.message !== '') {
        parts.push(payload.message);
      }

      if (typeof payload.errors === 'string' && payload.errors !== '') {
        parts.push(payload.errors);
      } else if (Array.isArray(payload.errors)) {
        parts = parts.concat(
          payload.errors.filter(function (entry) {
            return typeof entry === 'string' && entry !== '';
          })
        );
      } else if (payload.errors && typeof payload.errors === 'object') {
        Object.keys(payload.errors).forEach(function (key) {
          var entry = payload.errors[key];

          if (typeof entry === 'string' && entry !== '') {
            parts.push(entry);
            return;
          }

          if (Array.isArray(entry)) {
            entry.forEach(function (value) {
              if (typeof value === 'string' && value !== '') {
                parts.push(value);
              }
            });
          }
        });
      }

      return parts.join(' ').trim();
    }

    function isInventoryLimitError(payload) {
      var message = buildCartApiErrorText(payload).toLowerCase();

      if (!message) {
        return false;
      }

      /*
        Shopify can return different localized stock-limit phrases depending on
        the storefront language. Match only clear inventory/quantity patterns
        so generic network or server failures still use the normal error copy.
      */
      return (
        message.indexOf("can't add more") !== -1 ||
        message.indexOf('cannot add more') !== -1 ||
        message.indexOf('only have') !== -1 ||
        message.indexOf('not enough stock') !== -1 ||
        message.indexOf('sold out') !== -1 ||
        message.indexOf('inventory') !== -1 ||
        message.indexOf('mehr hinzuf') !== -1 ||
        message.indexOf('nur noch') !== -1 ||
        message.indexOf('bestand') !== -1 ||
        message.indexOf('nicht genug') !== -1 ||
        message.indexOf('ausverkauft') !== -1
      );
    }

    function normalizeFulfillmentPreference(value) {
      if (value === 'Flexible fulfillment') {
        return 'Allow replacement language if needed';
      }

      if (value === 'Exact order only') {
        return 'No replacement language, refund and ship remainder';
      }

      if (value === 'Ship remainder / substitutions allowed') {
        return 'Allow replacement language if needed';
      }

      if (value === 'Cancel entire order if issue') {
        return 'No replacement language, refund and ship remainder';
      }

      return value;
    }

    function buildSectionRequestPayload(root, requestBody) {
      var sectionId = root ? root.getAttribute('data-section-id') : '';
      var cartUrl = root ? root.getAttribute('data-cart-url') : '';
      var payload = {};

      Object.keys(requestBody || {}).forEach(function (key) {
        payload[key] = requestBody[key];
      });

      payload.sections = sectionId;
      payload.sections_url = cartUrl || '/cart';

      return payload;
    }

    function postCartMutation(url, payload, fallbackMessage) {
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            var requestError = new Error(parseCartApiError(data, fallbackMessage));
            requestError.userMessage = parseCartApiError(data, fallbackMessage);
            throw requestError;
          }

          return data;
        });
      });
    }

    /*
      Describes the focused control so the same control can be focused again
      after the root is swapped (matters in the drawer, where the panel stays).
    */
    function getFocusLocator(root) {
      var activeElement = document.activeElement;
      var lineElement;
      var attributes = [
        'data-cart-quantity-change',
        'data-cart-quantity-input',
        'data-cart-remove-line',
        'data-cart-promo-toggle',
        'data-cart-discount-input',
        'data-cart-discount-submit',
        'data-cart-checkout-button',
        'data-cart-drawer-close'
      ];
      var selector = '';

      if (!root || !activeElement || !root.contains(activeElement)) {
        return null;
      }

      attributes.some(function (attribute) {
        if (!activeElement.hasAttribute(attribute)) {
          return false;
        }

        selector = '[' + attribute + (activeElement.getAttribute(attribute) ? '="' + activeElement.getAttribute(attribute) + '"' : '') + ']';
        return true;
      });

      if (!selector && activeElement.matches('[data-cart-fulfillment-option]')) {
        selector = '[data-cart-fulfillment-option][value="' + activeElement.value + '"]';
      }

      if (!selector) {
        return null;
      }

      lineElement = activeElement.closest('[data-cart-line-key]');

      return {
        lineReference: lineElement ? lineElement.getAttribute('data-line-reference') : '',
        selector: selector
      };
    }

    function restoreFocus(root, locator) {
      var scope = root;
      var target;
      var panel = root ? root.closest('[data-cart-drawer-panel]') : null;

      if (!root) {
        return;
      }

      if (!locator) {
        /* An open drawer must keep focus inside the dialog. */
        if (panel && panel.closest('.is-open') && !panel.contains(document.activeElement)) {
          panel.focus({ preventScroll: true });
        }
        return;
      }

      if (locator.lineReference) {
        scope = root.querySelector('[data-line-reference="' + locator.lineReference + '"]');

        if (!scope) {
          /* The line is gone (removed): keep focus inside the dialog. */
          target = root.closest('[data-cart-drawer-panel]');

          if (target) {
            target.focus({ preventScroll: true });
          }
          return;
        }
      }

      target = scope.querySelector(locator.selector);

      if (target && !target.disabled) {
        target.focus({ preventScroll: true });
      } else if (root.closest('[data-cart-drawer-panel]')) {
        root.closest('[data-cart-drawer-panel]').focus({ preventScroll: true });
      }
    }

    function replaceCartSection(sectionHtml, options) {
      var currentRoot = getCartRoot();
      var parser = new DOMParser();
      var parsedDocument = parser.parseFromString(sectionHtml, 'text/html');
      var nextRoot = parsedDocument.querySelector('[data-cart-section-root]');
      var nextPromoInput;
      var currentBody;
      var nextBody;
      var scrollTop = 0;
      var focusLocator;

      if (!currentRoot || !nextRoot) {
        window.location.reload();
        return null;
      }

      currentBody = currentRoot.querySelector('[data-cart-drawer-body]');
      scrollTop = currentBody ? currentBody.scrollTop : 0;
      focusLocator = getFocusLocator(currentRoot);

      currentRoot.replaceWith(nextRoot);

      nextBody = nextRoot.querySelector('[data-cart-drawer-body]');

      if (nextBody && scrollTop) {
        nextBody.scrollTop = scrollTop;
      }

      restoreFocus(nextRoot, focusLocator);

      if (options && options.keepPromoOpen) {
        setPromoExpanded(nextRoot, true);
      }

      if (options && options.promoCode) {
        nextPromoInput = getPromoInput(nextRoot);

        if (nextPromoInput) {
          nextPromoInput.value = options.promoCode;
        }
      }

      if (options && options.promoErrorMessage) {
        setPromoError(nextRoot, options.promoErrorMessage);
      }

      syncCheckoutEligibility(nextRoot);
      syncAllLineQuantityControls(nextRoot);

      return nextRoot;
    }

    function applyRenderedSection(payload, options) {
      var root = getCartRoot();
      var sectionId = root ? root.getAttribute('data-section-id') : '';
      var sectionHtml = payload && payload.sections ? payload.sections[sectionId] : null;

      if (!sectionId || !sectionHtml) {
        window.location.reload();
        return Promise.resolve(null);
      }

      return Promise.resolve(replaceCartSection(sectionHtml, options));
    }

    function refreshCartSection() {
      var root = getCartRoot();
      var cartUrl = root ? root.getAttribute('data-cart-url') : '';

      if (!root || !cartUrl) {
        return Promise.resolve(null);
      }

      /* The drawer is a layout section: re-render just it. */
      if (root.hasAttribute('data-cart-drawer-root')) {
        return fetch(
          (root.getAttribute('data-cart-render-url') || '/') + '?sections=' + encodeURIComponent(root.getAttribute('data-section-id')),
          { headers: { Accept: 'application/json' } }
        )
          .then(function (response) {
            if (!response.ok) {
              throw new Error('cart-refresh');
            }

            return response.json();
          })
          .then(function (sections) {
            var sectionHtml = sections ? sections[root.getAttribute('data-section-id')] : null;

            return sectionHtml ? replaceCartSection(sectionHtml) : null;
          });
      }

      /*
        The cart page remains server-rendered. When a shared recommendation card
        adds an item to the Shopify cart, we fetch the canonical cart page HTML
        again and swap only the cart section root so totals, lines, and empty
        state always stay truthful to Shopify.
      */
      return fetch(cartUrl, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('cart-refresh');
          }

          return response.text();
        })
        .then(function (markup) {
          return replaceCartSection(markup);
        });
    }

    function refreshSharedCartCount() {
      var cartFeedback = window.TCGHiveCartFeedback;

      if (!cartFeedback || typeof cartFeedback.refreshCartCount !== 'function') {
        return Promise.resolve();
      }

      return cartFeedback.refreshCartCount().catch(function () {
        return null;
      });
    }

    function saveFulfillmentPreference(nextValue) {
      var root = getCartRoot();
      var updateUrl = root ? root.getAttribute('data-cart-update-url') : '';
      var fallbackError = root ? root.getAttribute('data-fulfillment-preference-error') : '';
      var savedMessage = root ? root.getAttribute('data-fulfillment-preference-saved') : '';
      var promoPanel = getPromoPanel(root);
      var promoInput = getPromoInput(root);
      var existingAttributes;
      var nextAttributes;
      var saveStatusTimeout;
      var focusedOptionValue = '';

      if (!root || !updateUrl || !nextValue || fulfillmentMutationPending) {
        return;
      }

      fulfillmentMutationPending = true;
      /* Disabling the radios drops focus, so remember which one had it. */
      focusedOptionValue =
        document.activeElement && document.activeElement.matches('[data-cart-fulfillment-option]')
          ? document.activeElement.value
          : '';
      clearStatus(root);
      clearFulfillmentStatus(root);
      setFulfillmentPending(root, true);

      existingAttributes = parseCartAttributes(root);
      nextAttributes = {};

      Object.keys(existingAttributes).forEach(function (key) {
        nextAttributes[key] = existingAttributes[key];
      });

      nextAttributes['Fulfillment preference'] = normalizeFulfillmentPreference(nextValue);

      postCartMutation(
        updateUrl,
        buildSectionRequestPayload(root, {
          attributes: nextAttributes
        }),
        fallbackError
      )
        .then(function (payload) {
          return applyRenderedSection(payload, {
            keepPromoOpen: promoPanel ? !promoPanel.hidden : false,
            promoCode: promoInput ? promoInput.value : ''
          }).then(function (nextRoot) {
            var activeRoot = nextRoot || root;

            setFulfillmentStatus(activeRoot, savedMessage, 'success');
            announce(activeRoot, savedMessage);

            saveStatusTimeout = window.setTimeout(function () {
              clearFulfillmentStatus(activeRoot);
            }, 2200);

            return saveStatusTimeout;
          });
        })
        .catch(function (error) {
          var activeRoot = getCartRoot() || root;
          var userMessage = (error && (error.userMessage || error.message)) || fallbackError;

          setFulfillmentStatus(activeRoot, userMessage, 'error');
          announce(activeRoot, userMessage);
        })
        .finally(function () {
          var activeRoot = getCartRoot() || root;

          fulfillmentMutationPending = false;
          setFulfillmentPending(activeRoot, false);
          syncCheckoutEligibility(activeRoot);

          if (focusedOptionValue && activeRoot && !activeRoot.contains(document.activeElement)) {
            getFulfillmentInputs(activeRoot).some(function (input) {
              if (input.value !== focusedOptionValue) {
                return false;
              }

              input.focus({ preventScroll: true });
              return true;
            });
          }
        });
    }

    function queueLineMutation(lineKey, nextQuantity, lineReference) {
      var root = getCartRoot();
      var changeUrl = root ? root.getAttribute('data-cart-change-url') : '';
      var fallbackError = root ? root.getAttribute('data-status-error') : '';
      var inventoryError = root ? root.getAttribute('data-status-inventory-error') : '';
      var lineElement = findLineElement(lineKey, lineReference);
      var entry = pendingMutations.get(lineKey);

      if (!root || !changeUrl || !lineElement) {
        return;
      }

      if (entry && entry.pending) {
        entry.queuedQuantity = nextQuantity;
        pendingMutations.set(lineKey, entry);
        return;
      }

      pendingMutations.set(lineKey, {
        pending: true,
        queuedQuantity: null,
        lineReference: lineReference
      });

      clearStatus(root);
      clearLineError(lineElement);
      setLinePending(lineElement, true);
      root.setAttribute('aria-busy', 'true');

      postCartMutation(
        changeUrl,
        buildSectionRequestPayload(root, {
          id: lineKey,
          quantity: nextQuantity
        }),
        inventoryError || fallbackError
      )
        .then(function (payload) {
          var successMessage = nextQuantity === 0 ? root.getAttribute('data-status-removed') : root.getAttribute('data-status-updated');

          return applyRenderedSection(payload).then(function (nextRoot) {
            announce(nextRoot || root, successMessage);
            return refreshSharedCartCount();
          });
        })
        .catch(function (error) {
          var activeRoot = getCartRoot() || root;
          var activeLine = findLineElement(lineKey, lineReference) || lineElement;
          var userMessage = (error && (error.userMessage || error.message)) || fallbackError;

          showStatus(activeRoot, userMessage);
          announce(activeRoot, userMessage);
          setLineError(activeLine, userMessage);
        })
        .finally(function () {
          var latestEntry = pendingMutations.get(lineKey);
          var queuedQuantity = latestEntry ? latestEntry.queuedQuantity : null;
          var nextLineElement;
          var nextLineKey;
          var nextLineReference = latestEntry ? latestEntry.lineReference : lineReference;
          var activeRoot = getCartRoot();

          pendingMutations.delete(lineKey);

          if (activeRoot) {
            activeRoot.removeAttribute('aria-busy');
          }

          nextLineElement = findLineElement(lineKey, nextLineReference);

          if (nextLineElement) {
            setLinePending(nextLineElement, false);
            clearLineError(nextLineElement);
            syncLineQuantityControls(nextLineElement);
          }

          if (queuedQuantity !== null && nextLineElement) {
            nextLineKey = nextLineElement.getAttribute('data-cart-line-key');
            queueLineMutation(nextLineKey, queuedQuantity, nextLineReference);
          }
        });
    }

    function hasAppliedDiscounts(cartPayload) {
      var hasCartLevelDiscounts =
        Array.isArray(cartPayload.cart_level_discount_applications) &&
        cartPayload.cart_level_discount_applications.length > 0;
      var hasLineLevelDiscounts =
        Array.isArray(cartPayload.items) &&
        cartPayload.items.some(function (lineItem) {
          return (
            Array.isArray(lineItem.line_level_discount_allocations) &&
            lineItem.line_level_discount_allocations.length > 0
          );
        });

      return hasCartLevelDiscounts || hasLineLevelDiscounts;
    }

    function handlePromoSubmit(formElement) {
      var root = getCartRoot();
      var updateUrl = root ? root.getAttribute('data-cart-update-url') : '';
      var promoInput = formElement.querySelector('[data-cart-discount-input]');
      var promoSubmit = formElement.querySelector('[data-cart-discount-submit]');
      var promoCode = promoInput ? promoInput.value.trim() : '';
      var fallbackError = root ? root.getAttribute('data-promo-error') : '';

      if (!root || !updateUrl || !promoInput) {
        return;
      }

      clearStatus(root);
      setPromoError(root, '');
      root.setAttribute('aria-busy', 'true');

      if (promoSubmit) {
        promoSubmit.disabled = true;
      }

      postCartMutation(
        updateUrl,
        buildSectionRequestPayload(root, {
          discount: promoCode
        }),
        fallbackError
      )
        .then(function (payload) {
          var discountApplied = promoCode === '' || hasAppliedDiscounts(payload);
          var promoErrorMessage = discountApplied ? '' : fallbackError;
          var promoSuccessMessage = root.getAttribute('data-promo-success');

          return applyRenderedSection(payload, {
            keepPromoOpen: promoCode !== '',
            promoCode: promoCode,
            promoErrorMessage: promoErrorMessage
          }).then(function (nextRoot) {
            var statusRoot = nextRoot || root;

            if (promoErrorMessage) {
              showStatus(statusRoot, promoErrorMessage);
              announce(statusRoot, promoErrorMessage);
              return null;
            }

            announce(statusRoot, promoSuccessMessage);
            clearStatus(statusRoot);
            return refreshSharedCartCount();
          });
        })
        .catch(function (error) {
          var activeRoot = getCartRoot() || root;
          var userMessage = (error && (error.userMessage || error.message)) || fallbackError;

          setPromoExpanded(activeRoot, true);
          setPromoError(activeRoot, userMessage);
          showStatus(activeRoot, userMessage);
          announce(activeRoot, userMessage);
        })
        .finally(function () {
          var activeRoot = getCartRoot();
          var activeSubmit = activeRoot ? activeRoot.querySelector('[data-cart-discount-submit]') : null;

          if (activeRoot) {
            activeRoot.removeAttribute('aria-busy');
          }

          if (activeSubmit) {
            activeSubmit.disabled = false;
          }
        });
    }

    document.addEventListener('click', function (event) {
      var root = getCartRoot();
      var promoToggle = event.target.closest('[data-cart-promo-toggle]');
      var removeLink = event.target.closest('[data-cart-remove-line]');
      var quantityButton = event.target.closest('[data-cart-quantity-change]');
      var lineElement;
      var currentQuantity;
      var step;
      var nextQuantity;
      var quantityDelta;
      var maxQuantity;
      var lineKey;
      var lineReference;

      if (!root) {
        return;
      }

      if (promoToggle) {
        event.preventDefault();
        setPromoExpanded(root, promoToggle.getAttribute('aria-expanded') !== 'true');
        return;
      }

      if (removeLink) {
        lineElement = removeLink.closest('[data-cart-line-key]');

        if (!lineElement || lineElement.getAttribute('aria-busy') === 'true') {
          return;
        }

        event.preventDefault();
        lineKey = lineElement.getAttribute('data-cart-line-key');
        lineReference = lineElement.getAttribute('data-line-reference');
        queueLineMutation(lineKey, 0, lineReference);
        return;
      }

      if (quantityButton) {
        lineElement = quantityButton.closest('[data-cart-line-key]');

        if (!lineElement || lineElement.getAttribute('aria-busy') === 'true') {
          return;
        }

        event.preventDefault();
        currentQuantity = getLineQuantity(lineElement);
        step = getLineStep(lineElement);
        quantityDelta = Number(quantityButton.getAttribute('data-cart-quantity-change')) || 0;
        maxQuantity = getLineMax(lineElement);
        nextQuantity = clampLineQuantity(
          lineElement,
          currentQuantity + quantityDelta * step
        );

        if (nextQuantity === currentQuantity) {
          if (quantityDelta > 0 && maxQuantity !== null && Number.isFinite(maxQuantity) && currentQuantity >= maxQuantity) {
            showInventoryLimitFeedback(root, lineElement);
          }

          syncLineQuantityControls(lineElement);
          return;
        }

        lineKey = lineElement.getAttribute('data-cart-line-key');
        lineReference = lineElement.getAttribute('data-line-reference');
        queueLineMutation(lineKey, nextQuantity, lineReference);
      }
    });

    document.addEventListener('change', function (event) {
      var fulfillmentInput = event.target.closest('[data-cart-fulfillment-option]');
      var quantityInput = event.target.closest('[data-cart-quantity-input]');
      var root = getCartRoot();
      var lineElement;
      var currentQuantity;
      var nextQuantity;
      var rawQuantity;
      var maxQuantity;

      if (fulfillmentInput) {
        syncCheckoutEligibility(root);
        clearFulfillmentStatus(root);
        saveFulfillmentPreference(fulfillmentInput.value);
        return;
      }

      if (!quantityInput) {
        return;
      }

      lineElement = quantityInput.closest('[data-cart-line-key]');

      if (!lineElement || lineElement.getAttribute('aria-busy') === 'true') {
        return;
      }

      currentQuantity = getLineQuantity(lineElement);
      rawQuantity = Number(quantityInput.value);
      maxQuantity = getLineMax(lineElement);
      nextQuantity = clampLineQuantity(lineElement, quantityInput.value);
      quantityInput.value = String(nextQuantity);
      syncLineQuantityControls(lineElement);

      if (Number.isFinite(rawQuantity) && maxQuantity !== null && Number.isFinite(maxQuantity) && rawQuantity > maxQuantity) {
        showInventoryLimitFeedback(root, lineElement);
      }

      if (nextQuantity === currentQuantity) {
        return;
      }

      queueLineMutation(
        lineElement.getAttribute('data-cart-line-key'),
        nextQuantity,
        lineElement.getAttribute('data-line-reference')
      );
    });

    document.addEventListener('submit', function (event) {
      var discountForm = event.target.closest('[data-cart-discount-form]');

      if (!discountForm) {
        return;
      }

      event.preventDefault();
      handlePromoSubmit(discountForm);
    });

    document.addEventListener('tcghive:cart:updated', function (event) {
      var root = getCartRoot();
      var detail = event && event.detail ? event.detail : {};
      var isDrawer = Boolean(root && root.hasAttribute('data-cart-drawer-root'));

      if (!root) {
        return;
      }

      if (detail.source !== 'shared-add-to-cart' && !(isDrawer && detail.source === 'shared-add-many-to-cart')) {
        return;
      }

      root.setAttribute('aria-busy', 'true');

      refreshCartSection()
        .catch(function () {
          if (!isDrawer) {
            window.location.reload();
          }
        })
        .finally(function () {
          var activeRoot = getCartRoot();

          if (activeRoot) {
            activeRoot.removeAttribute('aria-busy');
          }

          /* After an add, the drawer replaces the success toast. */
          if (isDrawer) {
            openDrawer();
          }
        });
    });

    /*
      Cart drawer shell: open/close, focus trap, Esc, scroll lock, header
      trigger, and the soft checkout gate. Everything is a no-op when the
      drawer is not rendered (cart page, setting off).
    */
    var drawer = document.querySelector('[data-cart-drawer]');
    var drawerPanel = drawer ? drawer.querySelector('[data-cart-drawer-panel]') : null;
    var drawerTrigger = null;

    function isDrawerOpen() {
      return Boolean(drawer && drawer.classList.contains('is-open'));
    }

    function getDrawerFocusables() {
      if (!drawerPanel) {
        return [];
      }

      return Array.prototype.filter.call(
        drawerPanel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        function (element) {
          return element.offsetParent !== null || element === document.activeElement;
        }
      );
    }

    function openDrawer(trigger) {
      if (!drawer || !drawerPanel) {
        return;
      }

      if (!isDrawerOpen()) {
        drawerTrigger = trigger || document.activeElement;
      }

      drawer.classList.add('is-open');
      document.documentElement.classList.add('cart-drawer-open');
      document.querySelectorAll('[data-cart-drawer-open]').forEach(function (button) {
        button.setAttribute('aria-expanded', 'true');
      });

      window.setTimeout(function () {
        if (!drawerPanel.contains(document.activeElement)) {
          drawerPanel.focus({ preventScroll: true });
        }
      }, 50);
    }

    function closeDrawer() {
      if (!isDrawerOpen()) {
        return;
      }

      drawer.classList.remove('is-open');
      document.documentElement.classList.remove('cart-drawer-open');
      document.querySelectorAll('[data-cart-drawer-open]').forEach(function (button) {
        button.setAttribute('aria-expanded', 'false');
      });

      if (drawerTrigger && typeof drawerTrigger.focus === 'function' && document.contains(drawerTrigger)) {
        drawerTrigger.focus({ preventScroll: true });
      }

      drawerTrigger = null;
    }

    function leadToFulfillment(root) {
      var fieldset = root ? root.querySelector('[data-cart-fulfillment-fieldset]') : null;
      var body = root ? root.querySelector('[data-cart-drawer-body]') : null;
      var firstOption = fieldset ? fieldset.querySelector('[data-cart-fulfillment-option]') : null;
      var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!fieldset) {
        return;
      }

      if (body) {
        body.scrollTo({
          top: Math.max(0, fieldset.offsetTop - body.offsetTop - 16),
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      }

      fieldset.classList.remove('is-attention');
      void fieldset.offsetWidth;
      fieldset.classList.add('is-attention');

      if (firstOption) {
        firstOption.focus({ preventScroll: true });
      }

      announce(root, root.getAttribute('data-fulfillment-preference-required'));
    }

    if (drawer) {
      document.addEventListener('click', function (event) {
        var trigger = event.target.closest('[data-cart-drawer-open]');
        var closeControl = event.target.closest('[data-cart-drawer-close]');
        var checkoutButton = event.target.closest('[data-cart-checkout-soft-gate]');

        if (trigger) {
          /* Modified clicks keep opening /cart (new tab, etc.). */
          if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
          }

          event.preventDefault();
          openDrawer(trigger);
          return;
        }

        if (closeControl && drawer.contains(closeControl)) {
          event.preventDefault();
          closeDrawer();
          return;
        }

        if (checkoutButton && checkoutButton.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          leadToFulfillment(checkoutButton.closest('[data-cart-section-root]'));
        }
      });

      document.addEventListener('keydown', function (event) {
        var focusables;
        var first;
        var last;

        if (!isDrawerOpen()) {
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          closeDrawer();
          return;
        }

        if (event.key !== 'Tab') {
          return;
        }

        focusables = getDrawerFocusables();

        if (!focusables.length) {
          event.preventDefault();
          drawerPanel.focus();
          return;
        }

        first = focusables[0];
        last = focusables[focusables.length - 1];

        if (event.shiftKey && (document.activeElement === first || document.activeElement === drawerPanel)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      window.TCGHiveCartDrawer = {
        open: openDrawer,
        close: closeDrawer,
        refresh: refreshCartSection
      };
    }

    syncCheckoutEligibility(getCartRoot());
    syncAllLineQuantityControls(getCartRoot());
  });
})();
